import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { joiResolver } from '@hookform/resolvers/joi';

import { useAppContext } from '../../../../app/Context';
import { useDrawerContext } from '../../../../components/Drawer/Context';
import { ClassListItem } from '../../../../db/classes/types';
import { CourseListItem } from '../../../../db/courses/types';
import { PollTypes, PreviouslyUsedPollItem, UnpublishedPollItem } from '../../../../db/polls/types';
import { PollSlideTypes, ToBeDone } from '../../../../db/types';
import routes from '../../../../routes/router';
import { FormValues } from './types';
import { validationSchema } from './validators';
import useUnpublishedPolls, { UnpublishedPoll } from './useUnpublishedPolls';
import usePreviouslyUsedPolls, { PreviouslyUsedPoll } from './usePreviouslyUsedPolls';
import { omit } from '../../../../utils/objects';

type SelectedUnpublishedPoll = UnpublishedPoll;
type SelectedPreviouslyUsedPoll = {
  courseData: DistributiveOmit<PreviouslyUsedPoll, 'pollType' | 'activities'>;
  classData: PreviouslyUsedPoll['activities'][number]['classData'];
  activityData: PreviouslyUsedPoll['activities'][number]['activityData'];
  pollType: PreviouslyUsedPoll['pollType'];
};

export type SelectedPoll = SelectedUnpublishedPoll | SelectedPreviouslyUsedPoll;

export interface UsePollsForm {
  selectedCourse: CourseListItem;
  selectedClass: ClassListItem;
  slideType: PollSlideTypes;
  onNext: (data: { selectedPoll: SelectedPoll }) => Promise<void> | void;
}

const usePollsForm = ({ selectedCourse, selectedClass, slideType, onNext }: UsePollsForm) => {
  const { session } = useAppContext();
  const { onClose } = useDrawerContext();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      selectedPollId: null,
    },
    mode: 'all',
  });

  const { watch } = formMethods;

  const { isFetchingUnpublishedPolls, unpublishedPolls } = useUnpublishedPolls({
    selectedCourse,
    selectedClass,
  });

  const { isFetchingPreviouslyUsedPolls, previouslyUsedPolls } = usePreviouslyUsedPolls();

  const pollDictionary = useRef<Dictionary<SelectedPoll>>({});

  const createPollDictionary = useCallback(() => {
    if (isFetchingUnpublishedPolls) return;
    if (isFetchingPreviouslyUsedPolls) return;

    const unpublishPollDictionary = unpublishedPolls.reduce((acc, unpublishPoll) => {
      acc[unpublishPoll.activityId] = unpublishPoll;
      return acc;
    }, {} as Dictionary<SelectedUnpublishedPoll>);

    const previouslyUsedPollsDictionary = previouslyUsedPolls.reduce((acc, previouslyUsedPoll) => {
      const { activities, pollType, ...courseData } = previouslyUsedPoll;

      for (let courseActivity of activities) {
        acc[courseActivity.activityData.activityId] = {
          courseData,
          classData: courseActivity.classData,
          activityData: courseActivity.activityData,
          pollType: pollType,
        };
      }

      return acc;
    }, {} as Dictionary<SelectedPreviouslyUsedPoll>);

    pollDictionary.current = { ...unpublishPollDictionary, ...previouslyUsedPollsDictionary };
  }, [isFetchingUnpublishedPolls, unpublishedPolls, isFetchingPreviouslyUsedPolls, previouslyUsedPolls]);

  useEffect(() => {
    createPollDictionary();
  }, [createPollDictionary]);

  const [selectedPoll, setSelectedPoll] = useState<SelectedPoll | null>(null);

  const selectedPollId = watch('selectedPollId');

  const getSelectedPoll = useCallback((pollId: MongoId | null) => {
    if (!pollId) return null;

    const selectedPoll = pollDictionary.current[pollId];
    return selectedPoll ?? null;
  }, []);

  useEffect(() => {
    const selectedPoll = getSelectedPoll(selectedPollId);
    setSelectedPoll(selectedPoll);
  }, [getSelectedPoll, selectedPollId]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      const selectedPoll = getSelectedPoll(data.selectedPollId);
      if (!selectedPoll) return;
      await onNext({
        selectedPoll,
      });
    },
    [getSelectedPoll, onNext]
  );

  const newPollUrl = useMemo(() => {
    if (!session?.university) return;
    return routes.newPoll.url({
      universitySlug: session.university.slug,
      courseId: selectedCourse.courseId,
      classId: selectedClass.classId,
      toBeDone: ToBeDone.IN_CLASS,
      slideType,
    });
  }, [session?.university, selectedCourse, selectedClass, slideType]);

  return {
    onClose,
    formMethods,
    onSubmit,
    isFetchingUnpublishedPolls,
    unpublishedPolls,
    isFetchingPreviouslyUsedPolls,
    previouslyUsedPolls,
    selectedPollType: selectedPoll?.pollType ?? null,
    newPollUrl,
  };
};

export default usePollsForm;
